'use client'

;!function(){try{var e="undefined"!=typeof globalThis?globalThis:"undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import {
  IntegrationUnavailableDialog,
  type IntegrationUnavailableCopy,
} from '@/components/integrations/shared/integration-unavailable-dialog'
import { loadExternalScript } from '@/components/integrations/shared/load-script'
import { intercomAppId } from '@/config/env.public'
import type { Locale } from '@/i18n/routing'

type IntercomContextValue = {
  open: () => void
  /** Closes the Messenger panel, if it is open. The launcher stays. */
  hide: () => void
  /**
   * Hides or restores the launcher. Intercom draws it at a z-index nothing else can reach, so on a
   * phone it lands on top of the demo planner — a chat bubble over the calendar someone opened to
   * pick a date.
   */
  setLauncherVisible: (visible: boolean) => void
  /**
   * The anonymous visitor's Intercom id (`Intercom('getVisitorId')`), or `undefined` while the
   * Messenger has not booted or answers nothing. It never boots the Messenger: a caller asks, and
   * sends what it gets.
   */
  visitorId: () => string | undefined
  /**
   * The visitor id once the Messenger has booted — bringing the deferred boot forward when it has
   * not, and waiting for it, bounded. For the planner: a booking made before the boot would reach
   * the Manager without the id and its lead would never be merged. Answers `undefined` when the
   * Messenger does not boot or name a visitor inside the bound; the caller carries on without it.
   */
  visitorIdAfterBoot: () => Promise<string | undefined>
}

const IntercomContext = createContext<IntercomContextValue | null>(null)
const intercomScriptId = 'intercom-widget-script'
/**
 * Bounds a load that never settles, so a later chat click downloads afresh instead of waiting on a
 * dead request. Generous: the widget is ~300 KB and usually loads in the background, unwatched.
 */
const intercomLoadTimeoutMs = 15_000
/**
 * How long a planner waits for the Messenger to boot and name its visitor before opening without
 * the id. The widget usually boots in well under a second once asked; the bound only matters when
 * it never arrives — a blocker, a dead network — and a visitor who asked for the planner must not
 * sit in front of nothing meanwhile.
 */
const messengerBootWaitMs = 4_000
/** How often the freshly booted widget is asked for its visitor id, until it answers. */
const visitorIdPollMs = 100

/**
 * Everything a logged-out visitor's Messenger is told, and nothing more — the workspace contract
 * (`docs/contracts/intercom/identity.md`): no user id, no JWT, no attributes.
 */
const messengerSettings = (hideLauncher: boolean, locale: Locale) => ({
  app_id: intercomAppId,
  hide_default_launcher: hideLauncher,
  language_override: locale,
})

/**
 * Set on the root domain by web while a tenant user's identified Messenger runs there (workspace
 * spec `2026-10-14-intercom-contact-identity.md`). Intercom keeps its session cookie on the root
 * domain too, so a user who never logged out would find the site's widget resuming their signed-in
 * conversation — and whatever they typed here would land on that user.
 */
const messengerCookie = 'offsoo_messenger'

function hasMessengerCookie() {
  return document.cookie.split(';').some((pair) => pair.trimStart().startsWith(`${messengerCookie}=`))
}

/**
 * Expires a cookie on the root domain and on the host alone. Web sets its cookies with
 * `Domain=<root>`, and the site always runs on that root's apex (`www.` is redirected away) —
 * offsoo.app, offsoo.dev, offsoo.test alike — so the page's own host is the domain to clear it on.
 * `appDomain` is not: a local build carries production's. The host-only write covers a host the
 * domain write cannot name (`127.0.0.1`).
 */
function expireCookie(name: string) {
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''
  const expired = `${name}=; Max-Age=0; Path=/; SameSite=Lax${secure}`
  document.cookie = `${expired}; Domain=${window.location.hostname}`
  document.cookie = expired
}

/**
 * Ends a session web's identified Messenger left behind, so the anonymous boot starts a visitor,
 * not that user. Runs before the widget script loads.
 *
 * `shutdown` alone does not end it: a widget that has loaded but not booted ignores it, and the
 * boot resumed the same visitor (measured with the real widget on offsoo.test over https,
 * 14 October 2026). A `shutdown` after a boot removes two cookies — Intercom's session and its
 * visitor id — so those are expired here, before the script loads and can read them; the fresh
 * boot then started a new visitor. The device id stays, as it does after Intercom's own
 * `shutdown`. `shutdown` is still called when a widget is already there.
 */
function endLeftBehindSession() {
  if (!hasMessengerCookie()) {
    return
  }
  window.Intercom?.('shutdown')
  expireCookie(`intercom-session-${intercomAppId}`)
  expireCookie(`intercom-id-${intercomAppId}`)
  expireCookie(messengerCookie)
}

export function IntercomProvider({
  locale,
  unavailableCopy,
  children,
}: {
  locale: Locale
  unavailableCopy: IntegrationUnavailableCopy
  children: ReactNode
}) {
  const bootedRef = useRef(false)
  /**
   * The page's language, handed to the Messenger as `language_override`. A logged-out visitor has
   * no Intercom language of their own, so without it the Messenger follows the workspace default,
   * Dutch, and greeted visitors on English pages in Dutch.
   */
  const localeRef = useRef(locale)
  /**
   * The launcher visibility last asked for through `setLauncherVisible`. Every write of
   * `intercomSettings` — the boot included — carries it, because the planner can ask before the
   * widget is there: a campaign link opens it on load, seconds before the deferred boot. A boot
   * that reset the launcher to visible put the chat bubble back on top of the calendar.
   */
  const launcherVisibleRef = useRef(true)
  const [loadFailed, setLoadFailed] = useState(false)

  const ensureIntercomLoaded = useCallback(() => {
    if (typeof window === 'undefined') {
      return Promise.resolve()
    }

    window.intercomSettings = messengerSettings(!launcherVisibleRef.current, localeRef.current)

    if (!bootedRef.current) {
      endLeftBehindSession()
    }

    if (window.Intercom && document.getElementById(intercomScriptId)) {
      return Promise.resolve()
    }

    return loadExternalScript({
      id: intercomScriptId,
      src: `https://widget.intercom.io/widget/${intercomAppId}`,
      timeoutMs: intercomLoadTimeoutMs,
    })
  }, [])

  // A locale switch keeps this provider mounted, so a booted Messenger is told the new language.
  useEffect(() => {
    localeRef.current = locale
    if (!bootedRef.current || !window.Intercom) {
      return
    }
    window.intercomSettings = messengerSettings(!launcherVisibleRef.current, locale)
    window.Intercom('update', window.intercomSettings)
  }, [locale])

  // The widget is ~300 KB of third-party JavaScript and the only external script on the critical
  // path. Nobody opens a chat in the first second of a page they have not read yet, so it waits
  // for a sign of life — a scroll, a pointer, a key — and for the browser to be idle. Anyone who
  // clicks a chat link before that gets it loaded on demand by `open()` below.
  useEffect(() => {
    let cancelled = false

    const boot = () => {
      if (cancelled) {
        return
      }
      void ensureIntercomLoaded()
        .then(() => {
          if (cancelled || !window.Intercom) {
            return
          }

          // Written again right before the boot: the planner may have asked to hide the launcher
          // while the script was still on its way, after `ensureIntercomLoaded` wrote the settings.
          window.intercomSettings = messengerSettings(!launcherVisibleRef.current, localeRef.current)

          if (bootedRef.current) {
            window.Intercom('update', window.intercomSettings)
            return
          }

          window.Intercom('boot', window.intercomSettings)
          bootedRef.current = true
        })
        .catch(() => undefined)
    }

    const events = ['pointerdown', 'keydown', 'scroll', 'touchstart'] as const
    // A visitor who reads without touching anything still gets the launcher, just not at load.
    const fallback = window.setTimeout(() => start(), 8000)
    const start = () => {
      // Once is enough: whichever comes first — a gesture or the fallback — starts the boot, and the
      // other must not run it a second time.
      window.clearTimeout(fallback)
      events.forEach((event) => window.removeEventListener(event, start))
      const idle = window.requestIdleCallback
      if (typeof idle === 'function') {
        idle(boot, { timeout: 2000 })
      } else {
        window.setTimeout(boot, 500)
      }
    }

    events.forEach((event) => window.addEventListener(event, start, { once: true, passive: true }))

    return () => {
      cancelled = true
      window.clearTimeout(fallback)
      events.forEach((event) => window.removeEventListener(event, start))
    }
  }, [ensureIntercomLoaded])

  /**
   * Boots the Messenger now, loading the widget first when it is not there. Shared by the chat's
   * `open()` and by `visitorIdAfterBoot()`; the deferred boot finds `bootedRef` set and only updates.
   */
  const bootNow = useCallback(
    () =>
      ensureIntercomLoaded().then(() => {
        if (!bootedRef.current && window.Intercom) {
          // The settings as they are now, not as they were when the load began: a planner opened
          // meanwhile has asked for the launcher to step aside, and the boot must honour it.
          window.intercomSettings = messengerSettings(!launcherVisibleRef.current, localeRef.current)
          window.Intercom('boot', window.intercomSettings)
          bootedRef.current = true
        }
      }),
    [ensureIntercomLoaded],
  )

  const readVisitorId = useCallback(() => {
    if (!bootedRef.current || !window.Intercom) {
      return undefined
    }
    const id = window.Intercom('getVisitorId')
    return typeof id === 'string' && isUuid(id) ? id : undefined
  }, [])

  const value = useMemo(
    () => ({
      setLauncherVisible: (visible: boolean) => {
        // Remembered even when the widget is not there yet, so the boot honours it.
        launcherVisibleRef.current = visible
        if (!window.Intercom) {
          return
        }
        window.intercomSettings = messengerSettings(!visible, localeRef.current)
        window.Intercom('update', window.intercomSettings)
      },
      open: () => {
        void bootNow()
          .then(() => window.Intercom?.('show'))
          // The visitor asked for the chat, so they hear that it did not come — often an ad blocker.
          // The background boot above stays silent: nobody asked for anything yet.
          .catch(() => setLoadFailed(true))
      },
      hide: () => {
        window.Intercom?.('hide')
      },
      visitorId: readVisitorId,
      visitorIdAfterBoot: () => {
        if (bootedRef.current) {
          return Promise.resolve(readVisitorId())
        }

        // Measured on offsoo.dev, 15 October 2026: a link that opens the planner on arrival ran
        // before the deferred boot, so the booking carried no id and its lead landed beside the
        // person for good. The boot is brought forward here, L2's cookie expiry included (it runs
        // inside `ensureIntercomLoaded`, before the script loads), and the planner waits for the
        // widget to name its visitor — briefly: whatever happens inside the bound, the planner
        // opens. A failed load stays silent here, as the background boot's does; the chat's own
        // `open()` is where a visitor hears about a widget that did not come.
        return new Promise<string | undefined>((resolve) => {
          let settled = false
          const settle = (id: string | undefined) => {
            if (settled) {
              return
            }
            settled = true
            window.clearTimeout(bound)
            resolve(id)
          }
          const bound = window.setTimeout(() => settle(readVisitorId()), messengerBootWaitMs)

          void bootNow()
            .then(() => {
              // The widget writes its visitor cookie as it boots; the first read may be a beat early.
              const poll = () => {
                if (settled) {
                  return
                }
                const id = readVisitorId()
                if (id) {
                  settle(id)
                } else {
                  window.setTimeout(poll, visitorIdPollMs)
                }
              }
              poll()
            })
            .catch(() => settle(undefined))
        })
      },
    }),
    [bootNow, readVisitorId],
  )

  return (
    <IntercomContext.Provider value={value} data-sentry-element="IntercomContext.Provider" data-sentry-component="IntercomProvider" data-sentry-source-file="intercom-provider.tsx">
      {children}
      <IntegrationUnavailableDialog open={loadFailed} onClose={() => setLoadFailed(false)} copy={unavailableCopy} data-sentry-element="IntegrationUnavailableDialog" data-sentry-source-file="intercom-provider.tsx" />
    </IntercomContext.Provider>
  )
}

/**
 * The Messenger's visitor id is a uuid, and the Manager validates `intercomVisitorId` as one
 * (ADR 0019): anything else would turn an otherwise valid email step, contact or booking into a 422.
 * The widget reads the id back from its own cookie without checking it, so the shape is checked
 * here, and a value that is not a uuid is left out — the form is worth more than the binding.
 */
const isUuid = (value: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)

export const intercomVisitor = (visitorId: string | undefined) => (visitorId ? { intercomVisitorId: visitorId } : {})

export function useIntercom() {
  const context = useContext(IntercomContext)
  if (!context) {
    throw new Error('useIntercom must be used within an IntercomProvider')
  }
  return context
}
